import { orderXClient } from '@/service/client'
import { unwrapServerMessage } from '@/service/server-message'
import {
  AdvancedTwapAlgoParams,
  AlgoServiceCommand,
  CrossExchangeArbParams,
  HedgeAlgoParams,
  LimitChaseAlgoParams,
  ScaleAlgoParams,
  SingleHedgeAlgoParams,
  SpreaderAlgoParams,
  SpreaderLegRole,
  StopAlgo,
  TwapAlgoParams,
} from '@/types/generated/algo'
import { ServerMessage } from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

// correlation_id lives on the StartAlgo wrapper (not on the algo-specific params), so callers
// pass it alongside the params and we lift it onto the command. Opaque id the service echoes
// back on refusal/error notifications (OrderExecutionError.correlation_id).
export type WithCorrelation<T> = T & { correlationId?: string }

export async function executeAlgo(payload: AlgoServiceCommand) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [payload],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function startTwapAlgo(payload: WithCorrelation<TwapAlgoParams>) {
  const { correlationId, ...twap } = payload
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [
            {
              startAlgo: {
                accountId: twap.accountId,
                twap,
                correlationId,
              },
            },
          ],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function startTwapAlgoBatch(payloads: WithCorrelation<TwapAlgoParams>[]) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: payloads.map(({ correlationId, ...twap }) => ({
            startAlgo: {
              accountId: twap.accountId,
              twap,
              correlationId,
            },
          })),
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function startLimitChaseAlgoBatch(payloads: WithCorrelation<LimitChaseAlgoParams>[]) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: payloads.map(({ correlationId, ...limitChase }) => ({
            startAlgo: {
              accountId: limitChase.accountId,
              limitChase,
              correlationId,
            },
          })),
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function startLimitChaseAlgo(payload: WithCorrelation<LimitChaseAlgoParams>) {
  const { correlationId, ...limitChase } = payload
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [
            {
              startAlgo: {
                accountId: limitChase.accountId,
                limitChase,
                correlationId,
              },
            },
          ],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

// Single instrument, single account — no batch variant. exchange/tradeVenue are left
// unspecified on the params (server-attached at the public-api boundary), same as plain
// TWAP's own start call.
export async function startAdvancedTwapAlgo(payload: WithCorrelation<AdvancedTwapAlgoParams>) {
  const { correlationId, ...advancedTwap } = payload
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [
            {
              startAlgo: {
                accountId: advancedTwap.accountId,
                advancedTwap,
                correlationId,
              },
            },
          ],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

// Single instrument, single account. account_id/exchange/trade_venue on the params are
// server-stamped from the authorized account, so only StartAlgo.accountId matters.
export async function startScaleAlgo(payload: WithCorrelation<ScaleAlgoParams>) {
  const { correlationId, ...scale } = payload
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [{ startAlgo: { accountId: scale.accountId, scale, correlationId } }],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function startHedgeAlgo(payload: WithCorrelation<HedgeAlgoParams>) {
  const { correlationId, ...hedge } = payload
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [
            {
              startAlgo: {
                accountId: hedge.accountId,
                hedge,
                correlationId,
              },
            },
          ],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function startSingleHedgeAlgo(payload: WithCorrelation<SingleHedgeAlgoParams>) {
  const { correlationId, ...singleHedge } = payload
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [
            {
              startAlgo: {
                accountId: singleHedge.accountId,
                singleHedge,
                correlationId,
              },
            },
          ],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

// Unlike Twap/LimitChase/Hedge, CrossExchangeArbParams carries no top-level accountId of its
// own — StartAlgo.accountId (the envelope's routing/owning account) is derived from leg 1's
// account, matching leg 1's role everywhere else as the "reference" leg.
export async function startCrossExchangeArbAlgo(payload: WithCorrelation<CrossExchangeArbParams>) {
  const { correlationId, ...crossExchangeArb } = payload
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [
            {
              startAlgo: {
                accountId: crossExchangeArb.legs[0]?.accountId ?? '',
                crossExchangeArb,
                correlationId,
              },
            },
          ],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

// StartAlgo.accountId is the quoting leg's account — every other server-derived field
// (stop_algo's account_ids[0], AlgoState.accountIds ordering) keys off the quoting leg too,
// per the Spreader integration guide §3.1/§5/§7, so this keeps one consistent "primary
// account" throughout the feature.
export async function startSpreaderAlgo(payload: WithCorrelation<SpreaderAlgoParams>) {
  const { correlationId, ...spreader } = payload
  const quotingAccountId =
    spreader.legs.find((leg) => leg.role === SpreaderLegRole.SPREADER_LEG_ROLE_QUOTING)
      ?.accountId ?? ''
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [
            {
              startAlgo: {
                accountId: quotingAccountId,
                spreader,
                correlationId,
              },
            },
          ],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}

export async function stopAlgo(payload: StopAlgo) {
  try {
    const { data } = await orderXClient.post<ServerMessage>('/api', {
      executionBatch: {
        algoCommands: {
          commands: [
            {
              stopAlgo: payload,
            },
          ],
        },
      },
    })
    return unwrapServerMessage(data)
  } catch (e) {
    const error = e as ErrorResponse
    return Promise.reject(error)
  }
}
